
import React, { useEffect, useMemo, useState } from "react";

import {
  getStudents,
  getAdminCourses,
  getAdminExams,
  getAdminPayments,
  getSchedules,
  getAdminRegistrations,
  normalizeDate,
} from "../api/adminclient";

import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";

function todayISO() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

// Groups a list of ISO date strings into "MMM" buckets for the last N months.
function lastNMonthsCounts(dates, months = 6) {
  const now = new Date();
  const buckets = [];

  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);

    buckets.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleString(undefined, { month: "short" }),
      count: 0,
    });
  }

  dates.forEach((iso) => {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return;

    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const bucket = buckets.find((b) => b.key === key);

    if (bucket) bucket.count += 1;
  });

  return buckets;
}

function MiniBarChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));

  return (
    <div className="d-flex align-items-end gap-3" style={{ height: 160 }}>
      {data.map((d) => (
        <div
          key={d.key}
          className="d-flex flex-column align-items-center flex-grow-1"
        >
          <div
            style={{
              width: "60%",
              height: `${Math.max(4, (d.count / max) * 120)}px`,
              background: "#2f6fed",
              borderRadius: "4px 4px 0 0",
            }}
            title={`${d.label}: ${d.count}`}
          />
          <small className="text-muted mt-1">{d.label}</small>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ segments, centerLabel, centerValue }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let cumulative = 0;

  const radius = 60;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="d-flex align-items-center gap-4 flex-wrap">
      <svg width="150" height="150" viewBox="0 0 150 150">
        <g transform="translate(75,75) rotate(-90)">
          {segments.map((seg) => {
            const fraction = seg.value / total;
            const dash = fraction * circumference;
            const offset = -(cumulative / total) * circumference;

            cumulative += seg.value;

            return (
              <circle
                key={seg.label}
                r={radius}
                cx="0"
                cy="0"
                fill="transparent"
                stroke={seg.color}
                strokeWidth="18"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </g>

        <text
          x="75"
          y="70"
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill="#14213d"
        >
          {centerValue}
        </text>

        <text
          x="75"
          y="90"
          textAnchor="middle"
          fontSize="11"
          fill="#6b7280"
        >
          {centerLabel}
        </text>
      </svg>

      <div className="d-flex flex-column gap-1">
        {segments.map((seg) => (
          <div
            key={seg.label}
            className="d-flex align-items-center gap-2 small"
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: seg.color,
                display: "inline-block",
              }}
            />
            {seg.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [payments, setPayments] = useState([]);

  // Additional data for accurate today's exam calculation.
  const [schedules, setSchedules] = useState([]);
  const [registrations, setRegistrations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [
          studentsResponse,
          coursesResponse,
          examsResponse,
          paymentsResponse,
          schedulesResponse,
          registrationsResponse,
        ] = await Promise.all([
          getStudents(),
          getAdminCourses(),
          getAdminExams(),
          getAdminPayments(),
          getSchedules(),
          getAdminRegistrations(),
        ]);

        const toArray = (response) => {
          const data = response?.data;

          if (Array.isArray(data)) return data;
          if (Array.isArray(data?.results)) return data.results;

          return [];
        };

        setStudents(toArray(studentsResponse));
        setCourses(toArray(coursesResponse));
        setExams(toArray(examsResponse));
        setPayments(toArray(paymentsResponse));
        setSchedules(toArray(schedulesResponse));
        setRegistrations(toArray(registrationsResponse));
      } catch (err) {
        console.error("Dashboard API error:", err);
        console.error("Response:", err.response?.data);

        setError(
          err.response
            ? `Django API error ${err.response.status}.`
            : "Cannot connect to Django. Check that the backend server is running."
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  // Calculate dashboard statistics.
  const stats = useMemo(() => {
    const activeExams = exams.filter(
      (e) => e.status === "Active"
    );

    const upcomingExams = exams.filter(
      (e) => e.status === "Upcoming"
    );

    const successfulPayments = payments.filter(
      (p) => p.status === "SUCCESS"
    );

    return {
      totalStudents: students.length,

      activeStudents: students.filter(
        (s) => s.status !== "Inactive"
      ).length,

      totalExams: exams.length,
      activeExams: activeExams.length,
      upcomingExams: upcomingExams.length,

      successfulPayments: successfulPayments.length,
    };
  }, [students, exams, payments]);

  // Today's exams: use the same schedule and registration
  // relationship as the dedicated Today's Exams page.
  const todaysExamsCount = useMemo(() => {
    const today = todayISO();

    const getRelatedId = (value) => {
      if (value && typeof value === "object") {
        return (
          value.id ??
          value.pk ??
          value.examId ??
          value.exam_id ??
          null
        );
      }

      return value ?? null;
    };

    const getScheduleId = (registration) =>
      getRelatedId(
        registration.schedule ??
        registration.scheduleId ??
        registration.schedule_id
      );

    const getScheduleDate = (schedule) =>
      normalizeDate(
        schedule.exam_date ??
        schedule.examDate ??
        schedule.date
      );

    // Only include schedules that have at least one registration
    // and whose exam date is today.
    const todayScheduleIds = new Set(
      registrations
        .map(getScheduleId)
        .filter((id) => id !== null && id !== undefined)
        .map(String)
    );

    // Count unique exams, not individual student registrations.
    const todayExamIds = new Set();

    schedules.forEach((schedule) => {
      const scheduleId = String(schedule.id ?? schedule.pk);

      if (
        !todayScheduleIds.has(scheduleId) ||
        getScheduleDate(schedule) !== today
      ) {
        return;
      }

      const examId = getRelatedId(
        schedule.exam ??
        schedule.examId ??
        schedule.exam_id
      );

      if (examId !== null && examId !== undefined) {
        todayExamIds.add(String(examId));
      } else {
        // If no exam ID is available, count the schedule itself.
        todayExamIds.add(`schedule-${scheduleId}`);
      }
    });

    return todayExamIds.size;
  }, [schedules, registrations]);

  const registrationData = useMemo(
    () =>
      lastNMonthsCounts(
        students.map((s) => s.registeredAt).filter(Boolean),
        6
      ),
    [students]
  );

  const examStatusSegments = useMemo(() => {
    const counts = {
      Active: 0,
      Upcoming: 0,
      Completed: 0,
      Inactive: 0,
    };

    exams.forEach((e) => {
      if (counts[e.status] !== undefined) {
        counts[e.status] += 1;
      }
    });

    return [
      { label: "Active", value: counts.Active, color: "#2f6fed" },
      { label: "Upcoming", value: counts.Upcoming, color: "#f5a623" },
      { label: "Completed", value: counts.Completed, color: "#1c8a5a" },
      { label: "Inactive", value: counts.Inactive, color: "#cbd5e1" },
    ];
  }, [exams]);

  const statCards = [
    {
      label: "Total Students",
      value: stats.totalStudents,
      icon: "bi-people",
    },
    {
      label: "Active Students",
      value: stats.activeStudents,
      icon: "bi-person-check",
    },
    {
      label: "Total Exams",
      value: stats.totalExams,
      icon: "bi-journal-check",
    },
    {
      label: "Active Exams",
      value: stats.activeExams,
      icon: "bi-lightning-charge",
    },
    {
      label: "Upcoming Exams",
      value: stats.upcomingExams,
      icon: "bi-calendar-event",
    },
    {
      label: "Today's Exams",
      value: todaysExamsCount,
      icon: "bi-calendar-day",
    },
    {
      label: "Courses",
      value: courses.length,
      icon: "bi-mortarboard",
    },
    {
      label: "Successful Payments",
      value: stats.successfulPayments,
      icon: "bi-cash-stack",
    },
  ];

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />

      <div className="admin-content">
        <AdminTopbar />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="h5 fw-bold mb-0">Dashboard</h2>

            <p className="text-muted small mb-0">
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning py-2 small">
            {error}
          </div>
        )}

        {loading && (
          <p className="text-muted">Loading dashboard...</p>
        )}

        {!loading && (
          <>
            <div className="row g-3 mb-3">
              {statCards.map((c) => (
                <div
                  className="col-6 col-md-4 col-lg-3"
                  key={c.label}
                >
                  <div className="admin-stat-card">
                    <div className="admin-stat-icon">
                      <i className={`bi ${c.icon}`} />
                    </div>

                    <div className="admin-stat-value">
                      {c.value}
                    </div>

                    <div className="admin-stat-label">
                      {c.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-3">
              <div className="col-12 col-lg-7">
                <div className="admin-card p-3 p-md-4 h-100">
                  <h3 className="h6 fw-bold mb-3">
                    Student Registrations (Last 6 Months)
                  </h3>

                  <MiniBarChart data={registrationData} />
                </div>
              </div>

              <div className="col-12 col-lg-5">
                <div className="admin-card p-3 p-md-4 h-100">
                  <h3 className="h6 fw-bold mb-3">
                    Exam Status
                  </h3>

                  <DonutChart
                    segments={examStatusSegments}
                    centerValue={stats.totalExams}
                    centerLabel="Exams"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
